import {object, ObjectSchema, string} from "yup";
import {TFunction} from "i18next";
import {formatDateShort, parseDate} from "../dateTime/dateFormatter";
import {DateTime} from "luxon";

export interface ValidityInfo {
  validFrom?: string | null,
  validTo?: string | null
}

export function validityInfoSchema(t: TFunction): ObjectSchema<ValidityInfo> {
  return object({
    validFrom: isoDateSchema(t),
    validTo: isoDateSchema(t)
      // Checked here instead of min/max on the pickers, which would hide years outside the range in the calendar (SSO-298)
      .test('notBeforeValidFrom', t('FormErrors.validToBeforeValidFrom') + '', function (validTo) {
        const validFrom = this.parent.validFrom;
        return !validFrom || !validTo || validFrom <= validTo;
      })
  })
}

function isoDateSchema(t: TFunction) {
  return string().nullable()
    .transform((str: string | null | undefined) => str?.substring(0, 10))
    .test('isoDate', t('FormErrors.invalidDate') + '', str => !str || DateTime.fromISO(str).isValid)
    .default(null);
}

/**
 * Whether the validity period applies today - the same check the server does to decide if a user may be enabled.
 */
export function currentlyValid(validity: ValidityInfo | undefined | null): boolean {
  if (!validity) {
    return true;
  }
  const today = DateTime.now().startOf('day');
  const validFrom = parseDate(validity.validFrom ?? undefined)?.startOf('day');
  const validTo = parseDate(validity.validTo ?? undefined)?.startOf('day');
  return (!validFrom || validFrom <= today) && (!validTo || validTo >= today);
}

export function formatValidity(validity: ValidityInfo, t: TFunction) {
  if (!validity.validFrom && !validity.validTo) {
    return t("ValidityInfo.unlimited");
  }
  if (!validity.validFrom && validity.validTo) {
    return t("common.until") + " " + formatDateShort(parseDate(validity.validTo));
  }
  if (validity.validFrom && !validity.validTo) {
    return t("common.from") + " " + formatDateShort(parseDate(validity.validFrom));
  }

  const fromDate = validity.validFrom ? formatDateShort(parseDate(validity.validFrom)) : t("common.from");
  const toDate = validity.validTo ? formatDateShort(parseDate(validity.validTo)) : t("common.to");
  return fromDate + " - " + toDate;
}
